import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getCommunityStrategies,
  bookmarkStrategy,
  unbookmarkStrategy,
} from "../services/strategyService";
import type { StrategyListItem } from "../services/strategyService";
import { useBacktestStore } from "../stores/backtestStore";
import { useAuthStore } from "../stores/authStore";
import { PageHeader } from "../components/PageHeader";
import { InvestModal } from "../components/InvestModal";
import { LoginPromptModal } from "../components/LoginPromptModal";
import OptimizationConfigModal from "../components/optimizer/OptimizationConfigModal";
import { FEATURES } from "../featureFlags";
import {
  MetricsFilterPanel,
  getDefaultFilters,
  countActiveFilters,
  applyMetricsFilters,
  type MetricsFilters,
} from "../components/MetricsFilterPanel";

// Strategy category gradients
const categoryGradients: Record<string, string> = {
  momentum: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
  value: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
  growth: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
  dividend: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
  bond: "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
  crypto: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
  default: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
};

const getCategoryFromStrategy = (name: string): string => {
  const lowerName = name.toLowerCase();
  if (lowerName.includes("momentum") || lowerName.includes("trend"))
    return "momentum";
  if (lowerName.includes("value") || lowerName.includes("fundamental"))
    return "value";
  if (lowerName.includes("growth") || lowerName.includes("tech"))
    return "growth";
  if (lowerName.includes("dividend") || lowerName.includes("income"))
    return "dividend";
  if (lowerName.includes("bond") || lowerName.includes("fixed")) return "bond";
  if (lowerName.includes("crypto") || lowerName.includes("bitcoin"))
    return "crypto";
  return "default";
};

export function SearchPage() {
  const navigate = useNavigate();
  const { reset } = useBacktestStore();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [strategies, setStrategies] = useState<StrategyListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [investModalStrategy, setInvestModalStrategy] =
    useState<StrategyListItem | null>(null);
  const [loginPromptAction, setLoginPromptAction] = useState<
    "bookmark" | "invest" | "optimize" | null
  >(null);
  const [optimizeStrategy, setOptimizeStrategy] = useState<{
    name: string;
    code: string;
    metrics?: {
      cagr?: number;
      sharpe?: number;
      max_drawdown?: number;
      sortino?: number;
    };
  } | null>(null);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [metricsFilters, setMetricsFilters] = useState<MetricsFilters>(
    getDefaultFilters()
  );
  const activeFilterCount = countActiveFilters(metricsFilters);

  const handleStrategyClick = (strategy: StrategyListItem) => {
    // Navigate to the strategy detail page to load code
    reset(); // Clear previous results and errors
    navigate(`/backtest/${strategy.id}`);
  };

  const handleBookmark = async (
    e: React.MouseEvent,
    strategy: StrategyListItem,
  ) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      setLoginPromptAction("bookmark");
      return;
    }
    try {
      if (strategy.is_bookmarked) {
        await unbookmarkStrategy(strategy.id);
      } else {
        const resp = await bookmarkStrategy(strategy.id);
        // If a copy was created, the bookmark now points to the copy
        if (resp.strategy_copy) {
          console.log(`[Bookmark] Private copy created: ${resp.strategy_copy.id}`);
        }
      }
      // Update local state
      setStrategies((prev) =>
        prev.map((s) =>
          s.id === strategy.id ? { ...s, is_bookmarked: !s.is_bookmarked } : s,
        ),
      );
    } catch (err) {
      console.error("Failed to toggle bookmark:", err);
    }
  };

  const handleInvest = (e: React.MouseEvent, strategy: StrategyListItem) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      setLoginPromptAction("invest");
      return;
    }
    setInvestModalStrategy(strategy);
  };

  useEffect(() => {
    loadStrategies();
  }, []);

  const loadStrategies = async () => {
    try {
      const data = await getCommunityStrategies(100, 0);
      setStrategies(data.strategies || []);
    } catch (err) {
      console.error("Failed to load strategies:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const categories = [
    "All",
    "Momentum",
    "Value",
    "Growth",
    "Dividend",
    "Bond",
    "Crypto",
  ];

  const filteredStrategies = applyMetricsFilters(
    strategies.filter((s) => {
      const matchesSearch =
        s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (s.description?.toLowerCase().includes(searchQuery.toLowerCase()) ??
          false);
      const matchesCategory =
        !selectedCategory ||
        selectedCategory === "All" ||
        getCategoryFromStrategy(s.name) === selectedCategory.toLowerCase();
      return matchesSearch && matchesCategory;
    }),
    metricsFilters
  );

  return (
    <div>
      {/* Header with trading background */}
      <PageHeader
        title="Community Strategies"
        subtitle="Discover and backtest proven investment strategies"
        backgroundImage="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1920&q=80"
      />

      <div className="px-4 sm:px-6 lg:px-8 pb-8">
        {/* Search and filters */}
        <div className="mb-8 space-y-4">
          {/* Search bar */}
          <div className="relative max-w-xl">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg
                className="w-5 h-5 text-surface-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by name, description, or strategy type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-field w-full pl-12 py-3"
            />
          </div>

          {/* Category filters */}
          <div className="flex flex-wrap items-center gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() =>
                  setSelectedCategory(category === "All" ? null : category)
                }
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  (category === "All" && !selectedCategory) ||
                  selectedCategory === category
                    ? "bg-primary-500 text-white shadow-lg shadow-primary-500/25"
                    : "bg-surface-800/50 text-surface-300 hover:bg-surface-700/50 hover:text-surface-100 border border-surface-700/50"
                }`}
              >
                {category}
              </button>
            ))}

            {/* Divider */}
            <div className="w-px h-6 bg-surface-700/50 mx-2" />

            {/* Advanced Filters Button */}
            <button
              onClick={() => setIsFilterPanelOpen(true)}
              className={`relative px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                activeFilterCount > 0
                  ? "bg-brand-500/20 text-brand-400 border border-brand-500/50 shadow-lg shadow-brand-500/10"
                  : "bg-surface-800/50 text-surface-300 hover:bg-surface-700/50 hover:text-surface-100 border border-surface-700/50"
              }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
              Metrics
              {activeFilterCount > 0 && (
                <span className="flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-brand-500 text-white text-xs font-semibold">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Clear all filters button */}
            {activeFilterCount > 0 && (
              <button
                onClick={() => setMetricsFilters(getDefaultFilters())}
                className="px-3 py-2 rounded-full text-sm font-medium text-surface-400 hover:text-surface-200 hover:bg-surface-700/50 transition-all duration-200"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Results count */}
        <div className="mb-6 flex items-center justify-between">
          <p className="text-sm text-surface-400">
            Showing{" "}
            <span className="text-surface-200 font-medium">
              {filteredStrategies.length}
            </span>{" "}
            strategies
          </p>
        </div>

        {/* Strategy grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="spinner w-8 h-8" />
              <p className="text-surface-400">Loading strategies...</p>
            </div>
          </div>
        ) : filteredStrategies.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <svg
              className="w-16 h-16 text-surface-600 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-lg font-medium text-surface-300 mb-2">
              No strategies found
            </h3>
            <p className="text-surface-500 max-w-md">
              Try adjusting your search or filter criteria
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredStrategies.map((strategy, index) => {
              const category = getCategoryFromStrategy(strategy.name);
              const bgGradient =
                categoryGradients[category] || categoryGradients.default;

              return (
                <div
                  key={strategy.id}
                  className="strategy-card group animate-slide-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                  onClick={() => handleStrategyClick(strategy)}
                >
                  {/* Background gradient */}
                  <div
                    className="strategy-card-image"
                    style={{ background: bgGradient }}
                  />

                  {/* Content */}
                  <div className="relative z-10">
                    {/* Category badge */}
                    <div className="mb-4 flex gap-2 flex-wrap">
                      <span className="badge badge-info capitalize">
                        {category}
                      </span>
                      {strategy.is_trending && (
                        <span className="badge bg-orange-500/20 text-orange-400 border-orange-500/30">
                          🔥 Trending
                        </span>
                      )}
                      {strategy.is_community && (
                        <span className="badge bg-purple-500/20 text-purple-400 border-purple-500/30">
                          Community
                        </span>
                      )}
                      {strategy.is_featured && (
                        <span className="badge bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                          ⭐ Featured
                        </span>
                      )}
                    </div>

                    {/* Title and description */}
                    <h3 className="text-xl font-semibold text-surface-100 mb-2 group-hover:text-primary-400 transition-colors">
                      {strategy.name}
                    </h3>
                    <p className="text-surface-400 text-sm mb-6 line-clamp-2">
                      {strategy.description || "No description available"}
                    </p>

                    {/* Metrics */}
                    {strategy.cagr !== undefined &&
                    strategy.sharpe !== undefined &&
                    strategy.max_drawdown !== undefined ? (
                      <div className="grid grid-cols-3 gap-3 p-4 -mx-2 rounded-xl bg-surface-950/50 backdrop-blur-sm border border-surface-700/30">
                        <div className="text-center">
                          <div
                            className={`text-lg font-bold ${(strategy.cagr ?? 0) >= 0 ? "metric-value-positive" : "metric-value-negative"}`}
                          >
                            {(strategy.cagr ?? 0) >= 0 ? "+" : ""}
                            {(strategy.cagr ?? 0).toFixed(1)}%
                          </div>
                          <div className="text-xs text-surface-500 font-medium">
                            CAGR
                          </div>
                        </div>
                        <div className="text-center border-x border-surface-700/50">
                          <div className="text-lg font-bold text-surface-200">
                            {(strategy.sharpe ?? 0).toFixed(2)}
                          </div>
                          <div className="text-xs text-surface-500 font-medium">
                            Sharpe
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold metric-value-negative">
                            -{Math.abs(strategy.max_drawdown ?? 0).toFixed(1)}%
                          </div>
                          <div className="text-xs text-surface-500 font-medium">
                            Max DD
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 -mx-2 rounded-xl bg-surface-950/50 backdrop-blur-sm border border-surface-700/30 text-center">
                        <div className="flex items-center justify-center gap-2 text-primary-400">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span className="text-sm font-medium">
                            Run Backtest
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        onClick={(e) => handleBookmark(e, strategy)}
                        className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                          strategy.is_bookmarked
                            ? "bg-primary-500/20 text-primary-400 border border-primary-500/30"
                            : "bg-surface-800/50 text-surface-300 border border-surface-700/50 hover:bg-surface-700/50 hover:text-surface-100"
                        }`}
                      >
                        <svg
                          className="w-4 h-4 shrink-0"
                          fill={
                            strategy.is_bookmarked ? "currentColor" : "none"
                          }
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                          />
                        </svg>
                        {strategy.is_bookmarked ? "In Watchlist" : "Add to Watchlist"}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/backtest/${strategy.id}`);
                        }}
                        className="flex-1 min-w-0 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 transition-all whitespace-nowrap"
                      >
                        <svg
                          className="w-4 h-4 shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                          />
                        </svg>
                        Performance
                      </button>
                      <button
                        onClick={(e) => handleInvest(e, strategy)}
                        className="flex-1 min-w-0 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-primary-500 text-white hover:bg-primary-600 transition-all whitespace-nowrap"
                      >
                        <svg
                          className="w-4 h-4 shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Invest
                      </button>
                      {FEATURES.OPTIMIZER && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!isAuthenticated) {
                            setLoginPromptAction("optimize");
                            return;
                          }
                          try {
                            const { getCommunityStrategy } = await import("../services/strategyService");
                            const full = await getCommunityStrategy(strategy.id);
                            setOptimizeStrategy({
                              name: strategy.name,
                              code: full.code || "",
                              metrics: {
                                cagr: strategy.cagr,
                                sharpe: strategy.sharpe,
                                max_drawdown: strategy.max_drawdown,
                                sortino: strategy.sortino,
                              },
                            });
                          } catch (err) {
                            console.error("Failed to load strategy code for optimization:", err);
                          }
                        }}
                        className="flex-1 min-w-0 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-primary-500/20 text-primary-400 border border-primary-500/30 hover:bg-primary-500/30 transition-all whitespace-nowrap"
                        title="Optimize this strategy"
                      >
                        <svg
                          className="w-4 h-4 shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                          />
                        </svg>
                        Optimize
                      </button>
                      )}
                    </div>
                  </div>

                  {/* Hover gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl pointer-events-none" />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Invest Modal */}
      {investModalStrategy && (
        <InvestModal
          strategyId={investModalStrategy.id}
          strategyName={investModalStrategy.name}
          isOpen={!!investModalStrategy}
          onClose={() => setInvestModalStrategy(null)}
          onSuccess={() => {
            setInvestModalStrategy(null);
            // Optionally navigate to portfolio
            navigate("/portfolio");
          }}
        />
      )}

      {/* Login Prompt Modal */}
      <LoginPromptModal
        isOpen={!!loginPromptAction}
        onClose={() => setLoginPromptAction(null)}
        action={loginPromptAction || "bookmark"}
      />

      {/* Optimization Config Modal */}
      {optimizeStrategy && (
        <OptimizationConfigModal
          isOpen={!!optimizeStrategy}
          onClose={() => setOptimizeStrategy(null)}
          strategyCode={optimizeStrategy.code}
          strategyName={optimizeStrategy.name}
          currentMetrics={optimizeStrategy.metrics}
          onSubmit={(_jobId) => {
            setOptimizeStrategy(null);
            navigate("/optimizer/jobs?submitted=1");
          }}
        />
      )}

      {/* Metrics Filter Panel */}
      <MetricsFilterPanel
        isOpen={isFilterPanelOpen}
        onClose={() => setIsFilterPanelOpen(false)}
        filters={metricsFilters}
        onFiltersChange={setMetricsFilters}
        activeFilterCount={activeFilterCount}
      />
    </div>
  );
}
